import { useState, useEffect, useMemo, Fragment } from "react";
import { getDisplayCount, getEventName, getUserActivePlan, transformData } from "@helpers/profileHelper";
import styles from "./ProfileTabs.module.scss";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import ReactCountryFlag from "react-country-flag";
import GetInTouchPopup from "../ShipmentDistribution/GetInTouchPopup";
import { CLOUDINARY_INTEL_BASE_URL } from "@utils/enum";
import { getIntelUrl } from "@utils/intelUrl";
import { createCountryRedirectUrl, generateTrackingObject, getRedirectProfileType } from "@core/global/global-country-functions";
import { globalCountryDetailsDictionary } from "@core/global/global-country-details";
import { redirectingProfileEnum } from "@core/global/global-country-dictionary";
import { useSelector } from "react-redux";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { profileTypeEnum } from "../ProfileHeader/profile-enum";
import { cachingPanelData, companyEvents } from "@containers/CompanyProfile/utils/enum.js";
import { useLocation, useParams } from "react-router-dom";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { formatNumber } from "@utils/utils";
import DemoTouchPoint from "@routes/intel/onboarding-screens/onboarding/VideoOpenerStrip";
import { countryDetails, DATASET_TYPE_MAPPING, CountryCodeOneToOneMap, getDatasetMirrorSortGroup, getShipmentDatasetDisplayName } from "@core/dictionary/global";

const ProfileTabs = ({ handleTabClick, activeTabId, loading, profileTabData, shipmentDistributionData, isSalesForce, setViewShipmentClick, viewShipmentClick, shipmentDistributionLoader }) => {
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);
  const [showGetInTouchPopup, setShowGetInTouchPopup] = useState(false);
  let isFreightForwarder = useSelector((state) => state?.logisticsProfile?.isFreightForwarder);
  const { search: searchUrlParams, pathname } = useLocation();
  const { companyID, companyname } = useParams();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");
  const country = new URLSearchParams(searchUrlParams).get("country");
  let mergeId = new URLSearchParams(searchUrlParams).get("mergedID");
  const tab = new URLSearchParams(searchUrlParams).get("tab");
  const [notifyCountry, setNotifyCountry] = useState("");
  const { countDetails, tabDetails } = useSelector((state) => state.companyProfile);
  const { isParentSubs } = useSelector((state) => state.companyProfile);
  const { activePanelId: category = "" } = tabDetails || {};
  const profileRedirectionId = (countDetails && Array.isArray(countDetails.elastic_data_id) && countDetails.elastic_data_id.join("$$$")) || companyID;
  const profileName = companyname;
  const profileId = companyID;
  const companyName = countDetails.name;
  const creditDetails = useSelector((state) => state.userDetails.creditDetails);
  const isOnlyMirrorSubscribed = creditDetails?.data?.filter((item) => item?.product_id !== "trademo_MC" && item.navigate !== "upgrade").every((dataset) => dataset?.is_active && countryDetails[dataset?.product_id]?.name?.toLowerCase()?.includes("mirror"));

  // Sort (subscribed first → direct before mirror → doc_count) and drop the rows the list never
  // renders, so the direct→mirror separator can be positioned by array neighbours in the map below.
  const sortedShipmentDistribution = useMemo(() => [...(shipmentDistributionData || [])].sort((a, b) => Number(b.isSubscribed) - Number(a.isSubscribed) || Number(getDatasetMirrorSortGroup(a.countryCode, a.trade_direction)) - Number(getDatasetMirrorSortGroup(b.countryCode, b.trade_direction)) || b.doc_count - a.doc_count).filter(({ key = "", doc_count = 0 }) => !(key.toLowerCase() === "multicountry" && isOnlyMirrorSubscribed) && !!doc_count), [shipmentDistributionData, isOnlyMirrorSubscribed]);

  useEffect(() => {
    setIsSideDrawerOpen(false);
    setViewShipmentClick(false);
  }, [category, setViewShipmentClick]);

  const openDrawer = () => {
    if (["importers", "exporters"].includes(category)) {
      setIsSideDrawerOpen(!isSideDrawerOpen);
      setViewShipmentClick(!viewShipmentClick);
    }
    trademoTrackingEvents(trackingEventDictionary.companyProfilePageViewShipments, generateTrackingObject(country, pathname, getUserActivePlan()));
  };

  const viewShipmentsEvent = () => {
    trademoTrackingEvents(getEventName(profileTypeEnum.company, companyEvents.viewShipments), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
  };

  const handleViewClick = (isSubscribed, country, trade_direction, key = "US Imports") => {
    if (!isSubscribed) {
      setShowGetInTouchPopup(true);
      setNotifyCountry(transformData(key, true, false));
      return;
    }
    sessionStorage.removeItem("selectedCountry");
    let payloadCategory = getRedirectProfileType(category);
    let redirectUrl = createCountryRedirectUrl(country.toLowerCase(), trade_direction);
    if (!!isFreightForwarder && payloadCategory === redirectingProfileEnum.nvocc.key) {
      payloadCategory = redirectingProfileEnum.freightForwarder.key;
    }
    let url = getIntelUrl() + `${redirectUrl}?redirectingFrom=${profileTypeEnum.company}&redirectingProfile=${payloadCategory}&redirectingProfileIds=${profileRedirectionId}&profileName=${profileName}&profileId=${profileId}`;
    if (isParentSubs) {
      url += `&isSubsidiaryIncluded=${isParentSubs}`;
    }
    if (mergeId) {
      url += `&isMerged=true&mergedID=${mergeId}`;
      url += `&companyName=${companyName}`;
    }
    if (tab) {
      url += `&tab=${tab}`;
    }
    window.open(url, "_blank");
    viewShipmentsEvent();
  };

  return (
    <div className={`${styles.profileTabs} tw-flex tw-justify-between tw-items-center`}>
      <div className="tw-flex tw-gap-[20px] tw-items-center">
        <div className={styles.tabTexts}>
          {loading ? (
            <LoadingSkeleton width={"40rem"} height={"1rem"} />
          ) : (
            profileTabData.map((data) => {
              return (
                <span
                  className={`${styles.text} ${!data.count ? "tw-flex tw-items-center" : ""} ${activeTabId === data.id && styles.tabActive}`}
                  key={data.id}
                  onClick={() => {
                    handleTabClick(data.id);
                  }}>
                  {data?.iconBeforeUrl ? <img src={`${data.iconBeforeUrl}`} alt="tab-icon" /> : <></>}
                  {data.text}
                  {data.count ? <span className={`${styles.count} ${activeTabId === data.id && styles.activeCount}`}>{getDisplayCount(data.count, 2)}</span> : null}
                  {data?.iconAfterUrl ? <img src={`${data.iconAfterUrl}`} className="tw-ml-1 tw-mt-0.5" alt="new-icon" /> : <></>}
                </span>
              );
            })
          )}
        </div>
        {!isSalesForce && !loading && (category === "importers" || category === "exporters") && (
          <button className="tw-bg-primary-purple-500 tw-text-white tw-outline-none tw-border-none tw-rounded-lg tw-text-sm tw-font-semibold tw-py-2 tw-px-4 tw-flex-shrink-0" onClick={openDrawer}>
            {`View Shipments`}
            <span className="material-symbols-outlined tw-w-5">arrows_more_up</span>
          </button>
        )}
      </div>
      <div>
        {!loading ? (
          category === cachingPanelData.importers ? (
            <div className="tw-flex tw-ml-auto tw-flex-shrink-0">
              <DemoTouchPoint module="analyse_companyProfile_page_as_buyer" isViewed={true} />
            </div>
          ) : category === cachingPanelData.exporters ? (
            <div className="tw-flex tw-ml-auto tw-flex-shrink-0">
              <DemoTouchPoint module="analyse_companyProfile_page_as_supplier" isViewed={true} />
            </div>
          ) : (
            <></>
          )
        ) : (
          <></>
        )}
      </div>
      {["importers", "exporters"].includes(category) && shipmentDistributionData && (
        <div className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-my-12 tw-bg-white tw-shadow-sideDrawer tw-border-t tw-border-l tw-border-b tw-border-solid tw-border-sideDrawer tw-transition-all tw-duration-250 tw-ease-in-out tw-rounded-bl-[12px] tw-z-[10000] ${isSideDrawerOpen ? " tw-translate-x-0" : "tw-w-0  tw-translate-x-full"}`}>
          {isSideDrawerOpen && (
            <div className={styles.sideDrawerContent}>
              <div
                className={styles.closeButton}
                onClick={() => {
                  setIsSideDrawerOpen(false);
                  setViewShipmentClick(false);
                }}>
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                  <path d="M291-253.85 253.85-291l189-189-189-189L291-706.15l189 189 189-189L706.15-669l-189 189 189 189L669-253.85l-189-189-189 189Z" />
                </svg>
              </div>
              <p className={styles.heading}>
                {`Shipment distribution of ${
                  companyName
                    ? companyName
                        .split(" ")
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ")
                    : "Unknown"
                } as ${category === "importers" ? "Consignee" : category === "exporters" ? "Shipper" : ""} across data sources`}
              </p>
              {shipmentDistributionLoader ? (
                <LoadingSkeleton lines={5} lineHeight="20px" width="100%" shape="rectangle" />
              ) : shipmentDistributionData && shipmentDistributionData?.length > 0 ? (
                <div className={`${styles.countriesList} tw-h-[calc(100vh-142px)] tw-overflow-auto tw-pr-3`}>
                  {sortedShipmentDistribution.map(({ key = "", doc_count = 0, isSubscribed, countryCode, country, trade_direction }, index) => {
                    const prev = sortedShipmentDistribution[index - 1];
                    const isMirrorDataset = getDatasetMirrorSortGroup(countryCode, trade_direction) === 1;
                    const prevIsSubscribedDirect = prev && prev.isSubscribed && getDatasetMirrorSortGroup(prev.countryCode, prev.trade_direction) === 0;
                    // Separator only at the direct→mirror boundary within the subscribed block.
                    const showMirrorSeparator = isSubscribed && isMirrorDataset && prevIsSubscribedDirect;
                    return (
                      <Fragment key={key.split(" ").join("_")}>
                        {showMirrorSeparator ? <div className="tw-border-t tw-border-gray-200 tw-my-2" /> : null}
                        <div className={`${styles.country} ${shipmentDistributionData?.length === 2 && shipmentDistributionData.find((item) => item.key === "multicountry") && key === "multicountry" ? "d-none" : ""}`}>
                          {doc_count ? (
                            <div className={styles.left}>
                              {country.toLowerCase() === globalCountryDetailsDictionary.multi_country.country_small ? (
                                <>
                                  <span className="material-symbols-outlined globe-image-side-drawer">public</span>
                                  <span className={styles.countryName}>Universal Search</span>
                                </>
                              ) : (
                                <>
                                  {countryCode === "global" ? <span className="material-symbols-outlined globe-image-side-drawer">public</span> : CountryCodeOneToOneMap[countryCode.toLowerCase()] ? <ReactCountryFlag countryCode={CountryCodeOneToOneMap[countryCode.toLowerCase()]} svg /> : <ReactCountryFlag countryCode={countryCode} svg />}
                                  <span className={styles.countryName}>
                                    {getShipmentDatasetDisplayName(country, trade_direction)}
                                    {DATASET_TYPE_MAPPING[`${countryCode?.toLowerCase()}_${trade_direction?.toLowerCase()?.endsWith("s") ? trade_direction?.toLowerCase()?.slice(0, -1) : trade_direction?.toLowerCase()}`]}
                                  </span>
                                </>
                              )}
                              {!isSubscribed ? <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1684478309/intel/icons/Lightning-alt_t9mkjb.svg`} className={styles.boltImage} /> : null}
                            </div>
                          ) : (
                            <></>
                          )}
                          {doc_count ? (
                            <span onClick={() => handleViewClick(isSubscribed, countryCode, trade_direction, key)}>
                              <span className={styles.right}>{doc_count !== doc_count ? `${formatNumber(doc_count)} ${doc_count === 1 ? "Shipment" : "Shipments"}` : `${formatNumber(doc_count)} ${doc_count === 1 ? "Shipment" : "Shipments"}`}</span>
                              {doc_count !== doc_count && <span className="tw-text-sm"> of {formatNumber(doc_count)}</span>}
                            </span>
                          ) : (
                            <></>
                          )}
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              ) : null}
              {showGetInTouchPopup ? <GetInTouchPopup showGetInTouchPopup={showGetInTouchPopup} handleGetInTouchClose={() => setShowGetInTouchPopup(false)} profileType={profileTypeEnum.company} notifyCountry={notifyCountry} blockAccessType={"dataset"} /> : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileTabs;
